import { LevelType } from 'data/types';
import { areFootprintShapesEqual } from 'utils/footprint';
import { getFirstMainLevelIndex } from 'utils/footprint-auto-scale';
import { getLevelFootprintArea } from 'utils/footprint-polygons';
import { getEffectiveFootprintShapes } from 'utils/footprint-union';
import { getLevelsPhysicallyAbove } from 'utils/level-vertical-order';
import { countsTowardFootprintSquareFootage } from 'utils/structure';

type PresetTargetAreaInput = {
  structure: LevelType[];
  activeLevelUniqueId: string;
  totalSqft: number;
  fallbackArea: number;
};

export function getLevelsReceivingMirroredFootprint(
  structure: LevelType[],
  activeLevelUniqueId: string
): LevelType[] {
  const sourceLevel = structure[getFirstMainLevelIndex(structure)];
  const activeLevel = structure.find(
    (level) => level.uniqueId === activeLevelUniqueId
  );
  if (!activeLevel) return [];

  if (sourceLevel?.uniqueId === activeLevelUniqueId) {
    const previousShapes = getEffectiveFootprintShapes(activeLevel);
    return structure.filter((level) =>
      areFootprintShapesEqual(
        getEffectiveFootprintShapes(level),
        previousShapes
      )
    );
  }

  const unmodifiedLevelsAbove = getLevelsPhysicallyAbove(
    structure,
    activeLevelUniqueId
  ).filter((level) => getEffectiveFootprintShapes(level).length === 0);

  return [activeLevel, ...unmodifiedLevelsAbove];
}

export function getPresetTargetArea({
  structure,
  activeLevelUniqueId,
  totalSqft,
  fallbackArea
}: PresetTargetAreaInput): number {
  const receivingLevels = getLevelsReceivingMirroredFootprint(
    structure,
    activeLevelUniqueId
  );
  const countedReceivingLevels = receivingLevels.filter(
    countsTowardFootprintSquareFootage
  );
  const receivingCount = Math.max(countedReceivingLevels.length, 1);
  if (totalSqft <= 0) {
    return fallbackArea;
  }

  const receivingIds = new Set(receivingLevels.map((level) => level.uniqueId));
  const otherArea = structure.reduce((sum, level) => {
    if (receivingIds.has(level.uniqueId)) return sum;
    if (!countsTowardFootprintSquareFootage(level)) return sum;
    return sum + getLevelFootprintArea(level);
  }, 0);
  const remaining = totalSqft - otherArea;
  if (remaining <= 0) {
    return Math.max(1, Math.round(totalSqft / receivingCount));
  }
  return Math.max(1, Math.round(remaining / receivingCount));
}
